// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  type HeatMapDataProps,
  ResponsiveHeatMap,
  type HeatMapDatum,
} from '@nivo/heatmap';
import { type CorrMatrix as CorrelationMatrixValues } from '@shared/data-access/interfaces/api/ai/assets';
import { type FC, useMemo } from 'react';
import { ChartContainer } from '@shared/ui-kit/components';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface CorrelationMatrixProps {
  readonly values: CorrelationMatrixValues & { name?: string[] };
}

type HeatMapData = HeatMapDataProps<HeatMapDatum, Record<string, unknown>>['data'];

export const CorrelationMatrix: FC<CorrelationMatrixProps> = ({ values }) => {
  const { t } = useTranslation();
  const data = useMemo<HeatMapData>(
    () =>
      values.ticker.map((ticker, index) => ({
        id: ticker,
        name: values?.name?.[index] ?? ticker,
        data: values.correlation_cols[ticker].map((value, index) => ({
          x: values.ticker[index],
          y: Math.round(value * 100),
        })),
      })),
    [values],
  );

  const colors = useMemo(
    () =>
      ({
        type: 'sequential',
        interpolator: (t: number) => {
          if (t < 0.5) return `rgba(121, 130, 141, ${(0.555555 - t) / 0.555555})`;

          if (t <= 1) return `rgba(16, 114, 186, ${(t - 0.454545) / 0.555555})`;
        },
        minValue: -100,
        maxValue: 100,
      } as const),
    [],
  );

  const enableLabels = values.ticker.length <= 20;

  const axis = enableLabels
    ? {
        tickSize: 0,
        tickPadding: 8,
        tickRotation: 0,
        legend: '',
      }
    : null;

  const labels = useMemo(
    () =>
      Array.from(data, ({ id, name }) => (
        <div className="flex items-center justify-center" key={id}>
          <Tooltip title={name as string} placement="top">
            <span className="text-border-dark">{id}</span>
          </Tooltip>
        </div>
      )),
    [data],
  );

  return (
    <ChartContainer
      header={t('common.correlation-matrix', { ns: 'common' }) as string}
      popupName="charts.correlationMatrix"
      tooltip
    >
      <div className="py-4">
        <div className="grid grid-cols-[auto_1fr] grid-rows-[auto_450px] gap-2 [grid-template-areas:'._axis-x'_'axis-y_chart']">
          <div
            className={`grid grid-flow-col [grid-area:axis-x] ${
              axis ? '' : 'hidden'
            }`}
          >
            {labels}
          </div>
          <div className={`grid [grid-area:axis-y] ${axis ? '' : 'hidden'}`}>
            {labels}
          </div>
          <div className="overflow-hidden [grid-area:chart]">
            <ResponsiveHeatMap
              data-testid="responsive-heat-map"
              data={data}
              labelTextColor="#FFFFFF"
              colors={colors}
              margin={{ left: 0, top: 0, bottom: 0, right: 0 }}
              axisTop={null}
              axisLeft={null}
              theme={{
                axis: {
                  ticks: {
                    text: {
                      fontFamily: 'Poppins',
                      fontSize: 14,
                      fill: 'var(--border-dark)',
                      lineHeight: 14,
                    },
                  },
                },
                labels: {
                  text: {
                    fontFamily: 'Poppins',
                    fontSize: '1em',
                  },
                },
              }}
              enableLabels={enableLabels}
              animate={false}
            />
          </div>
        </div>
      </div>
    </ChartContainer>
  );
};
